/** G1（总览与运行壳）六屏的契约解析与纯逻辑（#4598）。
 *
 * 形状一律以后端为准（主人 2026-10-08 裁决）：端点见 `docs/design/操作台对接开发文档.md` §3.12，
 * 屏规格见 `docs/design/screens/{overview,halt,reports,logs,settings,strategy}.md`。
 * 判断（链路是否完整、事件级别、是否超出来源节奏、异常持续天数）全由服务端给出，这里只解析与排版。
 */

import { apiUrl, expectList, expectRecord, optionalNumber, optionalString, query, requireBoolean, requireNumber, requireString } from "../api/client";
import { ContractError } from "../api/errors";
import { parseLastManualRun, type LastManualRun } from "./chainRerun";

type Obj = Record<string, unknown>;

const obj = (value: unknown, path: string, what: string): Obj => expectRecord(value, apiUrl(path), what);
const str = (r: Obj, k: string, path: string): string => requireString(r, k, apiUrl(path));
const numv = (r: Obj, k: string, path: string): number => requireNumber(r, k, apiUrl(path));
const bool = (r: Obj, k: string, path: string): boolean => requireBoolean(r, k, apiUrl(path));
const optBool = (r: Obj, k: string): boolean | null => (typeof r[k] === "boolean" ? (r[k] as boolean) : null);
const strList = (r: Obj, k: string, path: string): string[] => {
  const value = r[k];
  if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
    throw new ContractError(`字段 \`${k}\` 应为字符串数组`, apiUrl(path), r);
  }
  return value as string[];
};

// ---- 每日链路五步 `GET /chain/daily` ----------------------------------------

export type StepStatus = "ok" | "failed" | "missing" | "not_due" | "unknown";
const STEP_STATUSES: StepStatus[] = ["ok", "failed", "missing", "not_due", "unknown"];

export interface BridgeLine {
  source: string;
  /** `upstream_no_data`：桥接跑了、产物 0 条，上游 T 日没有记录（#4795）。 */
  outcome: "bridged" | "no_signal" | "upstream_no_data" | "not_run" | "failed" | "not_due";
  accepted: number;
  rejected: number;
  no_order: number;
  cadence_days: number | null;
  /** 上游最新交易日：只有 `upstream_no_data` 带，未知为 null。 */
  upstream_latest_date: string | null;
  detail: string;
}

export interface RunLine {
  strategy_id: string;
  outcome: string;
  detail: string;
}

export interface ChainStep {
  step: string;
  label: string;
  status: StepStatus;
  last_ts_utc: string | null;
  detail: string;
  source: string;
  lag_days: number | null;
  missing_days: string[];
  per_line: BridgeLine[];
  per_strategy: RunLine[];
}

export interface DailyChain {
  trade_date: string;
  requested_date: string;
  is_trading_day: boolean;
  complete: boolean;
  missing_steps: string[];
  steps: ChainStep[];
  /** 该交易日最近一次经 `POST /chain/daily/run` 受理的重跑（E4 #5007），没有为 null。 */
  last_manual_run: LastManualRun | null;
}

const CHAIN = "/chain/daily";

export function parseDailyChain(body: unknown): DailyChain {
  const r = obj(body, CHAIN, "每日链路");
  const steps = expectList(r, "steps", apiUrl(CHAIN), "链路步").map((s): ChainStep => {
    const status = str(s, "status", CHAIN);
    if (!STEP_STATUSES.includes(status as StepStatus)) {
      throw new ContractError(`链路步状态 ${status} 不在约定取值内`, apiUrl(CHAIN), s);
    }
    return {
      step: str(s, "step", CHAIN),
      label: str(s, "label", CHAIN),
      status: status as StepStatus,
      last_ts_utc: optionalString(s, "last_ts_utc"),
      detail: str(s, "detail", CHAIN),
      source: str(s, "source", CHAIN),
      lag_days: optionalNumber(s, "lag_days"),
      missing_days: Array.isArray(s["missing_days"]) ? (s["missing_days"] as unknown[]).map(String) : [],
      per_line: Array.isArray(s["per_line"])
        ? expectList(s, "per_line", apiUrl(CHAIN), "桥接信号线").map((l) => ({
            source: str(l, "source", CHAIN),
            outcome: str(l, "outcome", CHAIN) as BridgeLine["outcome"],
            accepted: numv(l, "accepted", CHAIN),
            rejected: numv(l, "rejected", CHAIN),
            no_order: numv(l, "no_order", CHAIN),
            cadence_days: optionalNumber(l, "cadence_days"),
            upstream_latest_date: optionalString(l, "upstream_latest_date"),
            detail: str(l, "detail", CHAIN),
          }))
        : [],
      per_strategy: Array.isArray(s["per_strategy"])
        ? expectList(s, "per_strategy", apiUrl(CHAIN), "跑一天策略").map((l) => ({
            strategy_id: str(l, "strategy_id", CHAIN),
            outcome: str(l, "outcome", CHAIN),
            detail: str(l, "detail", CHAIN),
          }))
        : [],
    };
  });
  return {
    trade_date: str(r, "trade_date", CHAIN),
    requested_date: str(r, "requested_date", CHAIN),
    is_trading_day: bool(r, "is_trading_day", CHAIN),
    complete: bool(r, "complete", CHAIN),
    missing_steps: strList(r, "missing_steps", CHAIN),
    steps,
    last_manual_run: parseLastManualRun(r["last_manual_run"]),
  };
}

/** 链路步的显示色与文字（规格「空态」节：not_due 灰、missing 红、无信号中性）。 */
export function stepView(status: StepStatus): { tone: "ok" | "bad" | "neutral" | "warn"; text: string } {
  switch (status) {
    case "ok":
      return { tone: "ok", text: "完成" };
    case "failed":
      return { tone: "bad", text: "失败" };
    case "missing":
      return { tone: "bad", text: "未执行" };
    case "not_due":
      return { tone: "neutral", text: "未到点" };
    default:
      return { tone: "warn", text: "无从判定" };
  }
}

export function bridgeOutcomeView(outcome: BridgeLine["outcome"]): { tone: "ok" | "bad" | "neutral" | "warn"; text: string } {
  switch (outcome) {
    case "bridged":
      return { tone: "ok", text: "已桥接" };
    case "no_signal":
      return { tone: "neutral", text: "上游当天无信号" };
    case "upstream_no_data":
      return { tone: "warn", text: "上游无数据" };
    case "not_run":
      return { tone: "bad", text: "桥接没跑" };
    case "failed":
      return { tone: "bad", text: "失败" };
    default:
      return { tone: "neutral", text: "未到点" };
  }
}

// ---- 配置只读 `GET /config/simulation` / `GET /config/validation` ------------

export interface SimulationConfigView {
  path: string;
  digest: string;
  loaded_digest: string;
  matches_loaded: boolean;
  body: Obj;
  execution: { signal_at: string; fill_at: string; same_day_fill: boolean };
}

export function parseSimulationConfig(body: unknown): SimulationConfigView {
  const p = "/config/simulation";
  const r = obj(body, p, "撮合假设");
  const e = obj(r["execution"], p, "执行时点");
  return {
    path: str(r, "path", p),
    digest: str(r, "digest", p),
    loaded_digest: str(r, "loaded_digest", p),
    matches_loaded: bool(r, "matches_loaded", p),
    body: obj(r["body"], p, "撮合假设原文"),
    execution: { signal_at: str(e, "signal_at", p), fill_at: str(e, "fill_at", p), same_day_fill: bool(e, "same_day_fill", p) },
  };
}

export interface ValidationConfigView {
  path: string;
  digest: string;
  body: Obj;
  missing_invalidation: string[];
}

export function parseValidationConfig(body: unknown): ValidationConfigView {
  const p = "/config/validation";
  const r = obj(body, p, "验证判据");
  return {
    path: str(r, "path", p),
    digest: str(r, "digest", p),
    body: obj(r["body"], p, "验证判据原文"),
    missing_invalidation: strList(r, "missing_invalidation", p),
  };
}

export interface RiskLimitsView {
  path: string;
  digest: string;
  limits: Obj;
}

/** `GET /risk/limits` 原样形状（config/risk_limits.json + 指纹）；设置屏只读显示。 */
export function parseRiskLimitsRaw(body: unknown): RiskLimitsView {
  const p = "/risk/limits";
  const r = obj(body, p, "风险上限");
  return { path: str(r, "path", p), digest: str(r, "digest", p), limits: obj(r["limits"], p, "风险上限原文") };
}

// ---- 策略清单 `GET /strategies` ---------------------------------------------

export interface StrategyRow {
  strategy_id: string;
  source_line: string;
  starting_balance: number | null;
  inception_date: string | null;
  modes: string[];
  cadence_days: number | null;
  last_signal_date: string | null;
  last_inbox_date: string | null;
  stale: boolean | null;
  stale_reference_date: string | null;
}

export function parseStrategies(body: unknown): StrategyRow[] {
  const p = "/strategies";
  return expectList(body, "strategies", apiUrl(p), "策略清单").map((x) => ({
    strategy_id: str(x, "strategy_id", p),
    source_line: str(x, "source_line", p),
    starting_balance: optionalNumber(x, "starting_balance"),
    inception_date: optionalString(x, "inception_date"),
    modes: strList(x, "modes", p),
    cadence_days: optionalNumber(x, "cadence_days"),
    last_signal_date: optionalString(x, "last_signal_date"),
    last_inbox_date: optionalString(x, "last_inbox_date"),
    stale: optBool(x, "stale"),
    stale_reference_date: optionalString(x, "stale_reference_date"),
  }));
}

export interface InboxIndexRow {
  strategy_id: string;
  date: string;
  accepted: number;
  rejected: number;
  no_order: number;
}

export function parseInboxIndex(body: unknown): InboxIndexRow[] {
  const p = "/signals/inbox";
  return expectList(body, "inbox", apiUrl(p), "收件箱索引").map((x) => ({
    strategy_id: str(x, "strategy_id", p),
    date: str(x, "date", p),
    accepted: numv(x, "accepted", p),
    rejected: numv(x, "rejected", p),
    no_order: numv(x, "no_order", p),
  }));
}

/** 每条策略最近一次收件（按日期）。 */
export function latestInboxByStrategy(rows: InboxIndexRow[]): Map<string, InboxIndexRow> {
  const out = new Map<string, InboxIndexRow>();
  for (const row of rows) {
    const current = out.get(row.strategy_id);
    if (!current || row.date > current.date) out.set(row.strategy_id, row);
  }
  return out;
}

// ---- 事件 `GET /events`（SSE）与 `GET /events/history` -----------------------

export type EventLevel = "INFO" | "WARN" | "ERROR";

export interface KeelEvent {
  seq: number;
  ts_ns: number;
  type: string;
  run_id: string | null;
  payload: Obj;
  level: EventLevel;
  source: string;
}

export function parseEvent(raw: unknown, path = "/events"): KeelEvent {
  const r = obj(typeof raw === "string" ? (JSON.parse(raw) as unknown) : raw, path, "事件");
  const level = str(r, "level", path);
  if (level !== "INFO" && level !== "WARN" && level !== "ERROR") {
    throw new ContractError(`事件级别 ${level} 不在约定取值内`, apiUrl(path), r);
  }
  return {
    seq: numv(r, "seq", path),
    ts_ns: numv(r, "ts_ns", path),
    type: str(r, "type", path),
    run_id: optionalString(r, "run_id"),
    payload: obj(r["payload"], path, "事件载荷"),
    level,
    source: str(r, "source", path),
  };
}

export interface EventsHistory {
  events: KeelEvent[];
  next_before_seq: number | null;
  retention_days: number;
  oldest_file: string | null;
}

export function parseEventsHistory(body: unknown): EventsHistory {
  const p = "/events/history";
  const r = obj(body, p, "事件历史");
  return {
    events: expectList(r, "events", apiUrl(p), "事件历史").map((x) => parseEvent(x, p)),
    next_before_seq: optionalNumber(r, "next_before_seq"),
    retention_days: numv(r, "retention_days", p),
    oldest_file: optionalString(r, "oldest_file"),
  };
}

/** 把新到的事件并入列表：按 seq 去重、倒序；`seq` 不连续时插一行缺口（规格「补播缺口」不静默）。 */
export interface EventRow {
  kind: "event" | "gap";
  seq: number;
  event?: KeelEvent;
  missing?: number;
}

export function mergeEvents(rows: EventRow[], incoming: KeelEvent[], max = 2000): EventRow[] {
  const seen = new Set(rows.filter((r) => r.kind === "event").map((r) => r.seq));
  let top = rows.find((r) => r.kind === "event")?.seq ?? null;
  const fresh = [...incoming].filter((e) => !seen.has(e.seq)).sort((a, b) => a.seq - b.seq);
  const added: EventRow[] = [];
  for (const event of fresh) {
    if (top !== null && event.seq > top + 1) {
      added.push({ kind: "gap", seq: event.seq - 0.5, missing: event.seq - top - 1 });
    }
    added.push({ kind: "event", seq: event.seq, event });
    if (top === null || event.seq > top) top = event.seq;
  }
  return [...added.reverse(), ...rows].sort((a, b) => b.seq - a.seq).slice(0, max);
}

export function filterEvents(rows: EventRow[], level: EventLevel | "", source: string): EventRow[] {
  return rows.filter((row) => {
    if (row.kind === "gap") return !level && !source;
    const event = row.event as KeelEvent;
    return (!level || event.level === level) && (!source || event.source === source);
  });
}

/** 一句话描述事件载荷（不猜级别，只挑常用字段）。 */
export function describeEvent(event: KeelEvent): string {
  const p = event.payload;
  const parts: string[] = [];
  for (const key of ["strategy_id", "signal_id", "outcome", "client_order_id", "instrument_id", "status", "side", "last_qty", "last_px", "balanced", "checked", "phase", "mode", "trade_date", "operator", "reason", "label", "summary", "error"]) {
    const value = p[key];
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${key}=${typeof value === "object" ? JSON.stringify(value) : String(value)}`);
  }
  return parts.join(" · ") || JSON.stringify(p);
}

// ---- 日报 `GET /reports/daily` / `GET /reports/daily/{date}` -----------------

export interface ReportIndexRow {
  trade_date: string;
  status: string;
  headline_count: number;
  escalated_count: number | null;
  delivered: boolean;
}

export function parseReportIndex(body: unknown): ReportIndexRow[] {
  const p = "/reports/daily";
  return expectList(body, "reports", apiUrl(p), "日报索引").map((x) => ({
    trade_date: str(x, "trade_date", p),
    status: str(x, "status", p),
    headline_count: numv(x, "headline_count", p),
    escalated_count: optionalNumber(x, "escalated_count"),
    delivered: bool(x, "delivered", p),
  }));
}

export interface HeadlineItem {
  kind: string;
  strategy_id: string;
  detail: string;
  first_seen_date: string | null;
  consecutive_days: number | null;
  escalated: boolean | null;
}

export interface PnlSection {
  basis: string | null;
  total_pnl: number | null;
  realized_closed: number | null;
  unrealized: number | null;
  cash_change: number | null;
  commissions: number | null;
  slippage_cost: number | null;
  position_value: number | null;
}

export interface PositionLine {
  instrument_id: string;
  qty: number | null;
  avg_px: number | null;
  mark: number | null;
  value: number | null;
}

export interface StrategySection {
  pnl: PnlSection;
  positions: PositionLine[];
  exposure: { gross: number | null; net: number | null; long: number | null; short: number | null; as_pct_of_starting: number | null };
  risk: Obj | null;
  actions: Obj;
  reconciliation: { balanced: boolean | null; summary: string };
  no_run: boolean;
}

export interface Delivery {
  channel: string | null;
  ok: boolean | null;
  sent_utc: string | null;
  error: string | null;
  target: string | null;
}

export interface DailyReportView {
  trade_date: string;
  status: string;
  generated_utc: string | null;
  headline: HeadlineItem[];
  strategies: Record<string, StrategySection>;
  delivery: Delivery | null;
  summary_text: string | null;
  sources: Record<string, string>;
}

const numOrNull = (value: unknown): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return Number(value);
  return null;
};

/** 日报详情：字段对照 `someo_keel/report.py::_strategy_report` 的真产出；缺字段给 null，不补 0。 */
export function parseDailyReportView(body: unknown): DailyReportView {
  const p = "/reports/daily/{date}";
  const r = obj(body, p, "日报");
  const headline = expectList(r, "headline", apiUrl(p), "异常项").map((h) => ({
    kind: str(h, "kind", p),
    strategy_id: typeof h["strategy_id"] === "string" ? (h["strategy_id"] as string) : "",
    detail: str(h, "detail", p),
    first_seen_date: optionalString(h, "first_seen_date"),
    consecutive_days: optionalNumber(h, "consecutive_days"),
    escalated: optBool(h, "escalated"),
  }));
  const strategies: Record<string, StrategySection> = {};
  for (const [sid, value] of Object.entries(obj(r["strategies"], p, "策略分区"))) {
    const s = obj(value, p, `策略分区 ${sid}`);
    const pnl = typeof s["pnl"] === "object" && s["pnl"] !== null ? (s["pnl"] as Obj) : {};
    const note = typeof pnl["position_basis_note"] === "object" && pnl["position_basis_note"] !== null ? (pnl["position_basis_note"] as Obj) : {};
    const exposure = typeof s["exposure"] === "object" && s["exposure"] !== null ? (s["exposure"] as Obj) : {};
    const recon = typeof s["reconciliation"] === "object" && s["reconciliation"] !== null ? (s["reconciliation"] as Obj) : {};
    strategies[sid] = {
      pnl: {
        basis: optionalString(pnl, "basis"),
        total_pnl: numOrNull(pnl["total_pnl"]),
        realized_closed: numOrNull(pnl["realized_closed"]),
        unrealized: numOrNull(pnl["unrealized"]),
        cash_change: numOrNull(pnl["cash_change"]),
        commissions: numOrNull(pnl["commissions"]),
        slippage_cost: numOrNull(pnl["slippage_cost"]),
        position_value: numOrNull(note["value"]),
      },
      positions: Array.isArray(s["positions"])
        ? (s["positions"] as unknown[]).filter((x): x is Obj => typeof x === "object" && x !== null).map((x) => ({
            instrument_id: String(x["instrument_id"] ?? ""),
            qty: numOrNull(x["qty"]),
            avg_px: numOrNull(x["avg_px"]),
            mark: numOrNull(x["mark"]),
            value: numOrNull(x["value"]),
          }))
        : [],
      exposure: {
        gross: numOrNull(exposure["gross"]),
        net: numOrNull(exposure["net"]),
        long: numOrNull(exposure["long"]),
        short: numOrNull(exposure["short"]),
        as_pct_of_starting: numOrNull(exposure["as_pct_of_starting"]),
      },
      risk: typeof s["risk"] === "object" && s["risk"] !== null ? (s["risk"] as Obj) : null,
      actions: typeof s["actions"] === "object" && s["actions"] !== null ? (s["actions"] as Obj) : {},
      reconciliation: { balanced: optBool(recon, "balanced"), summary: typeof recon["summary"] === "string" ? (recon["summary"] as string) : "" },
      no_run: s["no_run"] === true,
    };
  }
  const d = typeof r["delivery"] === "object" && r["delivery"] !== null ? (r["delivery"] as Obj) : null;
  return {
    trade_date: str(r, "trade_date", p),
    status: str(r, "status", p),
    generated_utc: optionalString(r, "generated_utc"),
    headline,
    strategies,
    delivery: d
      ? { channel: optionalString(d, "channel"), ok: optBool(d, "ok"), sent_utc: optionalString(d, "sent_utc"), error: optionalString(d, "error"), target: optionalString(d, "target") }
      : null,
    summary_text: optionalString(r, "summary_text"),
    sources: typeof r["sources"] === "object" && r["sources"] !== null ? (r["sources"] as Record<string, string>) : {},
  };
}

/** 异常项排序：持续升级的排前（规格「首次与持续分两级，持续的排前」）。 */
export function sortHeadline(items: HeadlineItem[]): HeadlineItem[] {
  return [...items].sort((a, b) => Number(b.escalated === true) - Number(a.escalated === true) || (b.consecutive_days ?? 0) - (a.consecutive_days ?? 0));
}

/** 取最近一份日报作为「当日」（总览同源）。 */
export function latestReport(rows: ReportIndexRow[]): ReportIndexRow | null {
  return rows.reduce<ReportIndexRow | null>((best, row) => (!best || row.trade_date > best.trade_date ? row : best), null);
}

// ---- 告警 `GET /alerts`、纸上盘状态与控制事件 --------------------------------

export interface AlertRow {
  ts_utc: string;
  kind: string;
  summary: string;
  detail: string;
}

/** 告警：`detail` 后端是对象（`alerts.raise_alert`），这里转成一行文本，不丢内容。 */
export function parseAlertsLoose(body: unknown): AlertRow[] {
  const p = "/alerts";
  return expectList(body, "alerts", apiUrl(p), "告警").map((x) => ({
    ts_utc: str(x, "ts_utc", p),
    kind: str(x, "kind", p),
    summary: str(x, "summary", p),
    detail: typeof x["detail"] === "string" ? (x["detail"] as string) : x["detail"] === null || x["detail"] === undefined ? "" : JSON.stringify(x["detail"]),
  }));
}

export interface PaperStrategyState {
  halted: boolean;
  halt_reason: string | null;
  last_trade_date: string | null;
  halt_kind: string | null;
  halt_since_utc: string | null;
}

export interface PaperStateView {
  halted: boolean;
  updated_utc: string | null;
  strategies: Record<string, PaperStrategyState>;
}

export function parsePaperState(body: unknown): PaperStateView {
  const p = "/paper/state";
  const r = obj(body, p, "纸上盘停机状态");
  const strategies: Record<string, PaperStrategyState> = {};
  for (const [sid, value] of Object.entries(obj(r["strategies"], p, "按策略停机状态"))) {
    const s = obj(value, p, `策略 ${sid}`);
    strategies[sid] = {
      halted: bool(s, "halted", p),
      halt_reason: optionalString(s, "halt_reason"),
      last_trade_date: optionalString(s, "last_trade_date"),
      halt_kind: optionalString(s, "halt_kind"),
      halt_since_utc: optionalString(s, "halt_since_utc"),
    };
  }
  return { halted: bool(r, "halted", p), updated_utc: optionalString(r, "updated_utc"), strategies };
}

export interface ControlEvent {
  ts_ns: number;
  kind: string;
  strategy_id: string | null;
  operator: string | null;
  reason: string | null;
}

export function parseControlEvents(body: unknown): ControlEvent[] {
  const p = "/paper/events";
  return expectList(body, "events", apiUrl(p), "控制事件").map((x) => ({
    ts_ns: numv(x, "ts_ns", p),
    kind: str(x, "kind", p),
    strategy_id: optionalString(x, "strategy_id"),
    operator: optionalString(x, "operator"),
    reason: optionalString(x, "reason"),
  }));
}

/** 停机种类的中文标签（键与 `account.HALTED_REASONS` 一致）。 */
export function haltKindLabel(kind: string | null): string {
  switch (kind) {
    case null:
      return "—";
    case "manual":
      return "人工停机";
    case "reconciliation_break":
      return "对账不平";
    case "order_timeout":
      return "委托超时未回报";
    case "position_unknown":
      return "持仓状态不明";
    case "风险上限":
      return "风险上限";
    default:
      return kind;
  }
}

export interface OverrideRow {
  scope: string;
  operator: string;
  reason: string;
  expires_utc: string;
}

export function parseOverrides(body: unknown): OverrideRow[] {
  const p = "/risk/overrides";
  return expectList(body, "overrides", apiUrl(p), "临时放行").map((x) => ({
    scope: str(x, "scope", p),
    operator: str(x, "operator", p),
    reason: str(x, "reason", p),
    expires_utc: String(x["expires_utc"] ?? ""),
  }));
}

// ---- 取数函数 ---------------------------------------------------------------

export const readDailyChain = (date?: string): Promise<DailyChain> => query<unknown>(CHAIN, { date }).then(parseDailyChain);
export const readSimulationConfig = (): Promise<SimulationConfigView> => query<unknown>("/config/simulation").then(parseSimulationConfig);
export const readValidationConfig = (): Promise<ValidationConfigView> => query<unknown>("/config/validation").then(parseValidationConfig);
export const readRiskLimitsRaw = (): Promise<RiskLimitsView> => query<unknown>("/risk/limits").then(parseRiskLimitsRaw);
export const readStrategies = (): Promise<StrategyRow[]> => query<unknown>("/strategies").then(parseStrategies);
export const readInboxIndex = (): Promise<InboxIndexRow[]> => query<unknown>("/signals/inbox").then(parseInboxIndex);
export const readEventsHistory = (params: { level?: string; source?: string; before_seq?: number | null; limit?: number } = {}): Promise<EventsHistory> =>
  query<unknown>("/events/history", params).then(parseEventsHistory);
export const readReportIndex = (): Promise<ReportIndexRow[]> => query<unknown>("/reports/daily").then(parseReportIndex);
export const readReportView = (date: string): Promise<DailyReportView> =>
  query<unknown>(`/reports/daily/${encodeURIComponent(date)}`).then(parseDailyReportView);
export const readAlertsLoose = (date?: string): Promise<AlertRow[]> => query<unknown>("/alerts", { date }).then(parseAlertsLoose);
export const readPaperState = (): Promise<PaperStateView> => query<unknown>("/paper/state").then(parsePaperState);
export const readControlEvents = (): Promise<ControlEvent[]> => query<unknown>("/paper/events", { limit: 200 }).then(parseControlEvents);
export const readOverrides = (): Promise<OverrideRow[]> => query<unknown>("/risk/overrides").then(parseOverrides);

/** 美东当日（YYYY-MM-DD）。「今日」口径与后端链路判定一致（美东）。 */
export function todayEt(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** 总览「数据日期（非今日）」判定（#4813）：最近一份日报早于后端给的「最近已开跑交易日」才标。
 * 参照日取 `GET /chain/daily`（不带日期）的 `trade_date`——链路在 T+1 美东清晨跑 T，T+1 早上数据日期 = T 是正常的；
 * 前端不自带交易日历，参照日未知（链路端点未返回）时不标。 */
export function isStaleDataDate(dataDate: string | null | undefined, latestChainDate: string | null | undefined): boolean {
  if (!dataDate || !latestChainDate) return false;
  return dataDate < latestChainDate;
}

/** ISO 时间串缩成「MM-DD HH:MM」（UTC 原样，不换时区，避免看错日）。 */
export function shortUtc(value: string | null | undefined): string {
  if (!value) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  return match ? `${match[2]}-${match[3]} ${match[4]}:${match[5]}${value.endsWith("Z") || value.includes("+00:00") ? " UTC" : ""}` : value;
}

// ---- 口径一致性（设置屏「口径一致性」/ 策略屏「三处同一配置」共用，#4753） ------------

/** 一组同口径的运行：按模式 + 换算后的指纹分组。 */
export interface DigestGroup {
  mode: string;
  /** 换算到现行算法的指纹（后端 `config_digest_canonical`，旧后端没有时回落 `config_digest`）。 */
  digest: string;
  /** 组内出现过的归档原值（旧算法指纹会与 `digest` 不同）。 */
  recorded: string[];
  count: number;
  /** 与当前口径（`/status.config_digest`）一致；当前口径未知时为 null。 */
  consistent: boolean | null;
}

interface DigestRun {
  mode: string;
  config_digest: string;
  config_digest_canonical?: string;
}

/** 运行按（模式, 口径指纹）分组；`byMode=false` 时只按指纹分组（mode 取空串）。
 *
 * 比对用后端换算过的指纹：2026-10-09 起指纹不含起始资金，之前归档的纸上盘 run.json
 * 是旧算法——同口径的旧指纹已由后端换算成现行指纹，不许在这里被判成「不一致」。
 */
export function digestGroups(runs: readonly DigestRun[], current: string | null, byMode = true): DigestGroup[] {
  const groups = new Map<string, DigestGroup>();
  for (const run of runs) {
    const digest = run.config_digest_canonical ?? run.config_digest;
    const mode = byMode ? run.mode : "";
    const key = `${mode}\u0000${digest}`;
    const group = groups.get(key) ?? { mode, digest, recorded: [], count: 0, consistent: current ? digest === current : null };
    group.count += 1;
    if (!group.recorded.includes(run.config_digest)) group.recorded.push(run.config_digest);
    groups.set(key, group);
  }
  return [...groups.values()];
}
